<script setup lang="ts">
import { __ } from '@@/bits/intl'
import { numberToHumanSize } from '@@/bits/numbers_helper'
import OzBox, { OzBoxColors } from '@@/library/v4/components/OzBox.vue'
import OzContainedButton, {
  OzContainedButtonColorScheme,
  OzContainedButtonSizePreset,
} from '@@/library/v4/components/OzContainedButton.vue'
import OzIcon from '@@/library/v4/components/OzIcon.vue'
import OzLoadingSpinner from '@@/library/v4/components/OzLoadingSpinner.vue'
import OzMembershipTierGrid from '@@/library/v4/components/OzMembershipTierGrid.vue'
import { computed } from 'vue'

const props = withDefaults(
  defineProps<{
    variant: Variant
    type?: Type
    isWhiteboard: boolean
    isExpanded: boolean
    isLoadingPlanInformation: boolean
    advertisedWallsQuota: number
    advertisedFileSizeQuota: string
    advertisedWhiteboardFrameQuota: number
    isLoadingAdvertisedQuotas: boolean
    whiteboardFramesUsed?: number
    wallsUsed?: number
    fileSize?: number
    xChecked?: boolean
    isUserLoggedIn?: boolean
    continueUrl?: string
    colorScheme?: OzBoxColors // allow parent to override color scheme
  }>(),
  {
    type: Type.Personal,
    wallsUsed: undefined,
    whiteboardFramesUsed: undefined,
    fileSize: undefined,
    xChecked: false,
    isUserLoggedIn: undefined,
    continueUrl: undefined,
    colorScheme: undefined,
  },
)

const isPadletQuotaOrFileSizeVariant = computed<boolean>(
  () =>
    props.variant === Variant.UnderFrameQuota ||
    props.variant === Variant.AtFrameQuota ||
    props.variant === Variant.OverFrameQuota ||
    props.variant === Variant.UnderPadletQuota ||
    props.variant === Variant.AtPadletQuota ||
    props.variant === Variant.OverPadletQuota ||
    props.variant === Variant.FileSize,
)

const colorScheme = computed<OzBoxColors | null>(() => {
  if (props.colorScheme) return props.colorScheme
  if (isPadletQuotaOrFileSizeVariant.value) return OzBoxColors.Secondary
  if (props.variant === Variant.Continue) return OzBoxColors.Pink
  return null
})
</script>

<script lang="ts">
export enum Type {
  Library,
  Personal,
}

export enum Variant {
  UnderFrameQuota,
  AtFrameQuota,
  OverFrameQuota,
  UnderPadletQuota,
  AtPadletQuota,
  OverPadletQuota,
  FileSize,
  SignUp,
  Continue,
  Default,
}

export default {}
</script>

<template>
  <OzBox :color="colorScheme">
    <OzMembershipTierGrid
      :class="[
        colorScheme === OzBoxColors.Secondary
          ? 'text-dark-text-300 dark:text-light-text-300'
          : 'text-dark-text-100 dark:text-light-text-100',
        isExpanded && 'min-h-13',
        !isExpanded && 'min-h-25',
      ]"
      :is-expanded="isExpanded"
    >
      <template #first>
        <div class="flex items-center" :class="[!isExpanded && 'ps-1']">
          <p class="text-heading-3 font-semibold">
            <template v-if="type === Type.Personal">{{ __('Neon') }}</template>
            <template v-if="type === Type.Library">{{ __('Team Neon') }}</template>
          </p>
          <OzIcon
            v-if="xChecked"
            :size="16"
            class="text-grape-500 dark:text-canary-500 ms-2"
            name="checkmark_thicker"
          />
        </div>
      </template>
      <template #second>
        <div v-if="isLoadingAdvertisedQuotas" :class="[isExpanded && 'ms-4', !isExpanded && 'ms-2']">
          <OzLoadingSpinner :size="24" />
        </div>
        <div v-else class="text-body-posts" :class="[!isExpanded && 'ps-1']">
          <p v-if="type === Type.Personal">{{ __('%{wallQuota} padlets', { wallQuota: advertisedWallsQuota }) }}</p>
          <p v-if="type === Type.Library">
            {{ __('%{wallQuota} padlets /team', { wallQuota: advertisedWallsQuota }) }}
          </p>

          <slot name="secondaryBenefitText">
            <p v-if="isWhiteboard">
              {{ __('%{frameLimit} card limit', { frameLimit: advertisedWhiteboardFrameQuota }) }}
            </p>
            <p v-else>
              {{ __('%{fileSizeLimit}MB /upload', { fileSizeLimit: advertisedFileSizeQuota }) }}
            </p>
          </slot>
        </div>
      </template>
      <template #third>
        <div :class="[isExpanded && 'pe-0', !isExpanded && 'pe-1']">
          <div v-if="isLoadingPlanInformation" :class="[isExpanded && 'ms-4', !isExpanded && 'me-2']">
            <OzLoadingSpinner :size="24" />
          </div>
          <div v-else :class="['text-body-posts', isExpanded && 'text-start', !isExpanded && 'text-end']">
            <p>{{ __('Free') }}</p>
          </div>
        </div>
      </template>
      <template #fourth>
        <slot name="endContent">
          <div
            v-if="variant === Variant.UnderFrameQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{whiteboardFramesUsed} cards made', { whiteboardFramesUsed }) }}</p>
            <p class="font-semibold">{{ __('under limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.AtFrameQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{whiteboardFramesUsed} cards made', { whiteboardFramesUsed }) }}</p>
            <p class="font-semibold">{{ __('at limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.OverFrameQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{whiteboardFramesUsed} cards made', { whiteboardFramesUsed }) }}</p>
            <p class="font-semibold">{{ __('over limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.UnderPadletQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{wallsUsed} padlets made', { wallsUsed }) }}</p>
            <p class="font-semibold">{{ __('under limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.AtPadletQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{wallsUsed} padlets made', { wallsUsed }) }}</p>
            <p class="font-semibold">{{ __('at limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.OverPadletQuota"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{wallsUsed} padlets made', { wallsUsed }) }}</p>
            <p class="font-semibold">{{ __('over limit') }}</p>
          </div>
          <div
            v-if="variant === Variant.FileSize"
            :class="['text-end text-body-small', isExpanded && 'pe-2', !isExpanded && 'pe-1']"
          >
            <p>{{ __('%{fileSize} file', { fileSize: numberToHumanSize(fileSize) }) }}</p>
            <p class="font-semibold">{{ __('exceeds limit') }}</p>
          </div>
          <OzContainedButton
            v-if="variant === Variant.SignUp && type === Type.Personal"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="isUserLoggedIn ? __('Go to billing') : __('Sign up')"
            :href="isUserLoggedIn ? '/dashboard/settings/billing' : '/auth/signup'"
          />
          <OzContainedButton
            v-if="variant === Variant.SignUp && type === Type.Library"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :color-scheme="OzContainedButtonColorScheme.Blue"
            :text="isUserLoggedIn ? __('Create team') : __('Sign up')"
            :href="isUserLoggedIn ? '/account/create/team' : '/auth/signup'"
          />
          <OzContainedButton
            v-if="variant === Variant.Continue"
            data-testid="libraryNeonContinueButton"
            :size-preset="isExpanded ? OzContainedButtonSizePreset.H40px : OzContainedButtonSizePreset.H32px"
            :text="__('Continue')"
            :href="continueUrl"
          />
        </slot>
      </template>
    </OzMembershipTierGrid>
  </OzBox>
</template>
